'use client';
import { type ComponentPropsWithoutRef } from 'react';
import { Controller, useFormContext, type FieldPath, type FieldValues } from 'react-hook-form';

import { cn } from '@/slw/shared/lib/utils';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/slw/shared/ui/base-form';
import { Switch as BaseSwitch } from '@/slw/shared/ui/switch';

// ----------------------------------------------------------------------

interface CustomSwitchProps<TFieldValues extends FieldValues>
  extends Omit<ComponentPropsWithoutRef<typeof Controller>, 'name' | 'control' | 'render'>,
    Omit<ComponentPropsWithoutRef<typeof BaseSwitch>, 'name' | 'defaultValue'> {
  name: FieldPath<TFieldValues>;
  helperText?: string;
  containerClass?: string;
  label?: string;
  withError?: boolean;
  showMessError?: boolean;
  direction?: 'row' | 'column';
}

export const Switch = ({
  name,
  label,
  helperText,
  defaultValue,
  containerClass,
  className,
  withError = true,
  showMessError = true,
  direction = 'row',
  ...other
}: CustomSwitchProps<FieldValues>) => {
  const { control } = useFormContext();
  const directionClass =
    direction === 'row' ? 'gap-2 items-center flex space-y-0' : 'gap-1 space-y-1 flex flex-col items-start';

  return (
    <FormField
      control={control}
      defaultValue={defaultValue}
      name={name}
      render={({ field, fieldState: { error } }) => (
        <FormItem className={cn(directionClass, containerClass)}>
          {!!label && <FormLabel className={cn('whitespace-nowrap')}>{label}</FormLabel>}
          <FormControl>
            <BaseSwitch
              {...field}
              {...other}
              onChange={field.onChange}
              className={cn(className, error && withError && 'border-destructive focus-visible:ring-destructive')}
            />
          </FormControl>
          {helperText && withError && showMessError && (
            <p className='text-sm text-muted-foreground ml-1.5'>{helperText}</p>
          )}
          {error && withError && showMessError && <FormMessage>{error.message}</FormMessage>}
        </FormItem>
      )}
    />
  );
};
